// src/hooks/useUsersMap.js
//
// Hook partagé pour récupérer une map { user_id → { name, role, ... } }
// utilisée pour afficher les auteurs dans les blocs de traçabilité.
//
// Cache en mémoire : un seul fetch par session, même si le hook est appelé
// depuis plusieurs composants en parallèle.

import { useEffect, useState } from 'react';
import { adminAPI } from '../services/api';

// Cache module-level pour éviter N fetchs simultanés
let _cachedMap = null;
let _inFlightPromise = null;

async function loadUsers() {
  if (_cachedMap) return _cachedMap;
  if (_inFlightPromise) return _inFlightPromise;

  _inFlightPromise = adminAPI.listUsers()
    .then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      const map = {};
      list.forEach(u => {
        if (u?.id) {
          const fullName = [u.prenom, u.nom].filter(Boolean).join(' ').trim()
                         || u.full_name
                         || u.email
                         || 'Utilisateur';
          map[u.id] = {
            id:    u.id,
            name:  fullName,
            email: u.email || null,
            role:  u.role || null,
            site:  u.site || null,
          };
        }
      });
      _cachedMap = map;
      return map;
    })
    .catch(() => {
      // Si l'utilisateur courant n'a pas accès (ex: pas Super Admin), on renvoie une map vide
      _cachedMap = {};
      return _cachedMap;
    })
    .finally(() => {
      _inFlightPromise = null;
    });

  return _inFlightPromise;
}

/**
 * Retourne { usersMap, loading, getUser(id) }.
 * - usersMap : objet { user_id: { name, role, ... } } (peut être vide si pas chargé / pas accès)
 * - getUser(id) : raccourci, renvoie un user ou null
 */
export function useUsersMap() {
  const [usersMap, setUsersMap] = useState(_cachedMap || {});
  const [loading, setLoading]   = useState(!_cachedMap);

  useEffect(() => {
    let cancelled = false;
    if (_cachedMap) {
      setLoading(false);
      return;
    }
    loadUsers().then(map => {
      if (!cancelled) {
        setUsersMap(map);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const getUser = (id) => (id && usersMap[id]) || null;

  return { usersMap, loading, getUser };
}

/**
 * Hook simplifié : renvoie juste le nom (ou un fallback) pour un user_id donné.
 */
export function useUserName(userId, fallback = 'Utilisateur inconnu') {
  const { getUser, loading } = useUsersMap();
  if (loading) return '…';
  if (!userId) return fallback;
  return getUser(userId)?.name || fallback;
}
